// Error message mappings for different Firebase/Firestore errors
const errorMessages = {
  'permission-denied': "You don't have permission to perform this action.",
  'not-found': 'The requested data could not be found.',
  'already-exists': 'This data already exists.',
  'failed-precondition': 'Operation failed due to server state.',
  'resource-exhausted': 'Too many requests. Please try again later.',
  cancelled: 'Operation was cancelled.',
  unknown: 'An unknown error occurred.',
  'invalid-argument': 'Invalid data provided.',
  'deadline-exceeded': 'Operation timed out.',
  unauthenticated: 'Please sign in to continue.',
  unavailable: 'Service is temporarily unavailable.',
  'data-loss': 'Unrecoverable data loss or corruption.'
};

export const googleAIErrorMessages = {
  PERMISSION_DENIED: 'API key is invalid or missing required permissions.',
  INVALID_ARGUMENT:
    'The image format is not supported or the image is corrupted.',
  FAILED_PRECONDITION: 'The service is not properly configured.',
  RESOURCE_EXHAUSTED: 'API quota has been exceeded. Please try again later.',
  UNAVAILABLE:
    'The service is temporarily unavailable. Please try again later.',
  DEADLINE_EXCEEDED: 'The request took too long to process. Please try again.',
  CANCELLED: 'The request was cancelled.',
  INTERNAL: 'An internal error occurred. Please try again later.',
  DEFAULT: 'Failed to identify plant. Please try again with a clearer image.'
};

export const diseaseDetectionErrors = {
  QUALITY_INSUFFICIENT:
    'Image quality is insufficient for accurate analysis. Please provide a clearer, well-lit image showing the affected areas.',
  NO_PLANT_VISIBLE:
    'No plant detected in the image. Please ensure the plant is clearly visible.',
  INSUFFICIENT_SYMPTOMS:
    'Disease symptoms are not clearly visible. Please provide images showing the affected areas.',
  MULTIPLE_PLANTS:
    'Multiple plants detected. Please provide an image focusing on a single affected plant.',
  PROCESSING_ERROR: 'Error processing the image. Please try again.',
  API_ERROR: 'Service temporarily unavailable. Please try again later.',
  INVALID_IMAGE:
    'Invalid image format or content. Please try a different image.',
  AUTHORIZATION_ERROR:
    'Authorization failed. Please check your API credentials.',
  RATE_LIMIT_EXCEEDED: 'Rate limit exceeded. Please try again later.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  DEFAULT: 'An error occurred during disease detection. Please try again.'
};

export function getFirestoreErrorMessage(error) {
  // Extract error code from Firestore error
  const code = error?.code?.split('/')?.[1] || 'unknown';
  return errorMessages[code] || error.message || 'An error occurred';
}

export function handleFirestoreError(error, toast, customMessage = '') {
  console.error('Firestore Error:', error);

  const message = getFirestoreErrorMessage(error);

  toast({
    title: customMessage || 'Error',
    description: message,
    variant: 'destructive'
  });

  return message;
}

export function handleGoogleAIError(error) {
  console.error('Google AI Error:', error);

  // Handle specific Google AI error codes
  if (error?.status) {
    switch (error.status) {
      case 401:
        return 'Authentication failed. Please check your API key.';
      case 403:
        return 'Access denied. Please check your API permissions.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Service error. Please try again later.';
      default:
        return googleAIErrorMessages.DEFAULT;
    }
  }
  // Handle error codes from the Google AI response
  if (error?.code) {
    return googleAIErrorMessages[error.code] || googleAIErrorMessages.DEFAULT;
  }

  // Handle network errors
  if (!navigator.onLine) {
    return 'No internet connection. Please check your network and try again.';
  }

  // Handle other errors
  return error?.message || googleAIErrorMessages.DEFAULT;
}

export function handleDiseaseDetectionError(error) {
  console.error('Disease Detection Error:', error);

  // Check for specific error types
  if (error?.message?.includes('QUALITY_INSUFFICIENT')) {
    return diseaseDetectionErrors.QUALITY_INSUFFICIENT;
  }

  if (error?.message?.includes('NO_PLANT_VISIBLE')) {
    return diseaseDetectionErrors.NO_PLANT_VISIBLE;
  }

  // Handle API-specific errors
  if (error?.status) {
    switch (error.status) {
      case 400:
        return diseaseDetectionErrors.INVALID_IMAGE;
      case 401:
        return diseaseDetectionErrors.AUTHORIZATION_ERROR;
      case 429:
        return diseaseDetectionErrors.RATE_LIMIT_EXCEEDED;
      case 500:
        return diseaseDetectionErrors.API_ERROR;
      default:
        return diseaseDetectionErrors.DEFAULT;
    }
  }

  // Handle network errors
  if (!navigator.onLine) {
    return diseaseDetectionErrors.NETWORK_ERROR;
  }

  // Default error message
  return error?.message || diseaseDetectionErrors.DEFAULT;
}
